"use client";

import { useState } from "react";
import { Control } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input"; // Assuming you have an Input component
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { ThreeDotsLoader } from "../loaders";
import { cn } from "@/lib/utils";

interface MultiSelectInputFieldProps {
  control: Control<any>; // React Hook Form control
  name: string;
  options: { label: string; value: number, prependLabelChildren?: React.ReactNode, appendLabelChildren?: React.ReactNode}[];
  label?: string;
  placeholder: string;
  loading: boolean;
  className?: string;
  disabled?: boolean;
};

export function MultiSelectInputField({
  control,
  name,
  options,
  label,
  placeholder,
  loading,
  className,
  disabled,
}: MultiSelectInputFieldProps) {

  // States
  const [search, setSearch] = useState("");

  // Hooks
  const { t } = useTranslation();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // Filter options based on the search query
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => {
        const handleToggle = (value: number) => {
          const isSelected = field.value?.includes(value);
          const updatedValues = isSelected
            ? field.value.filter((v: number) => v !== value)
            : [...(field.value || []), value];

          // Update the form field value
          field.onChange(updatedValues);
        };

        return (
          <FormItem className={cn(className ?? "form-item")}>
            {label &&
              <FormLabel>{label}</FormLabel>
            }
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="w-full">
                <Button variant="outline" disabled={disabled}>
                  {placeholder} {field.value?.length > 0 ? `: ${field.value.length} ${t("newtender.formgroups.operationalorganizations.selectedsuffix")}` : ""}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[300px] max-w-[580px] md:min-w-[400px] w-auto">
                <DropdownMenuSeparator />
                {loading ? (
                  <div className="w-full justify-center flex">
                    <ThreeDotsLoader
                      visible={loading}
                      color="#94A3B8"
                      radius={20}
                      height={12}
                      width={12}
                      ariaLabel="three-dots-loading"
                    />
                  </div>
                ) : (
                  <>
                    <div className="p-2">
                      <Input
                        placeholder={t("newtender.formgroups.operationalorganizations.searchplaceholder")}
                        value={search}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => {
                          // Prevent DropdownMenu from intercepting keystrokes
                          e.stopPropagation();
                        }}
                        className="w-full" />
                    </div>
                    <DropdownMenuSeparator />
                    <div className="max-h-[300px] overflow-y-auto">
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                          <DropdownMenuCheckboxItem
                            key={option.value}
                            checked={field.value?.includes(option.value)}
                            onCheckedChange={() => handleToggle(option.value)}
                            onSelect={(event) => event.preventDefault()}
                            className="flex gap-2"
                          >
                            {option.prependLabelChildren}
                            {option.label}
                            {option.appendLabelChildren}
                          </DropdownMenuCheckboxItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">
                          {t("newtender.formgroups.operationalorganizations.searchnoresults")}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
